<template>
  <div class="cm">
    <div class="cm-shell">
      <!-- HEADER -->
      <header class="cm-head">
        <span class="cm-eyebrow">Panel de administración</span>
        <h1>Carga <span class="serif">masiva</span></h1>
        <p class="cm-sub">Subí imágenes o alojamientos por Excel, y consultá usuarios y direcciones sin salir de esta pantalla.</p>
      </header>

      <div class="cm-grid">
        <!-- ============ MAIN ============ -->
        <section class="cm-main">
          <!-- SELECTOR DE TARJETAS -->
          <div class="cm-cards">
            <button type="button" class="cm-card" :class="{ active: activeMode === 'images' }" @click="activeMode = 'images'">
              <span class="cm-card-icon"><i class="fas fa-images"></i></span>
              <span class="cm-card-body">
                <strong>Carga de imágenes</strong>
                <span>Subí hasta 100 imágenes nombradas por idalojamiento.</span>
              </span>
              <span class="cm-card-arrow"><i class="fas fa-arrow-right"></i></span>
            </button>

            <button type="button" class="cm-card" :class="{ active: activeMode === 'excel' }" @click="activeMode = 'excel'">
              <span class="cm-card-icon"><i class="fas fa-file-excel"></i></span>
              <span class="cm-card-body">
                <strong>Carga vía Excel</strong>
                <span>Creá o actualizá alojamientos en masa con un CSV.</span>
              </span>
              <span class="cm-card-arrow"><i class="fas fa-arrow-right"></i></span>
            </button>
          </div>

          <!-- PLACEHOLDER -->
          <div v-if="!activeMode" class="cm-panel cm-empty">
            <i class="fas fa-hand-pointer"></i>
            <p>Seleccioná una de las dos opciones para comenzar.</p>
          </div>

          <!-- PANEL: IMÁGENES -->
          <div v-else-if="activeMode === 'images'" class="cm-panel">
            <h2>Carga masiva de imágenes</h2>
            <p class="cm-panel-desc">Subí imágenes de varios alojamientos a la vez (hasta 100 archivos). El nombre de cada archivo debe comenzar con el <b>idalojamiento</b> y puede incluir sufijos como "(1)", por ejemplo: <i>6899.jpg</i>, <i>6899 (1).jpg</i>, <i>69076.png</i>.</p>

            <div class="cm-actions">
              <label class="cm-file">
                <input type="file" @change="handleFilesSelect" accept="image/*" multiple hidden />
                <i class="fas fa-plus"></i> Elegir imágenes
              </label>
              <button class="cm-btn" :disabled="imgUploading || selectedFiles.length === 0" @click="uploadSelectedFiles">
                {{ imgUploading ? 'Subiendo...' : 'Subir imágenes' }}
              </button>
              <button class="cm-btn cm-btn-ghost" :disabled="imgUploading || selectedFiles.length === 0" @click="clearSelection">Limpiar</button>
            </div>

            <div v-if="selectedFiles.length" class="cm-hint">Seleccionadas: <strong>{{ selectedFiles.length }}</strong> imágenes</div>
            <div v-if="imgError" class="cm-alert cm-alert-error">{{ imgError }}</div>
            <div v-if="imgSuccess" class="cm-alert cm-alert-ok">Carga finalizada.</div>

            <div v-if="imgResultados.exitosos.length || imgResultados.errores.length" class="cm-results">
              <div v-if="imgResultados.exitosos.length" class="cm-results-block">
                <span class="cm-results-title ok">Imágenes subidas ({{ imgResultados.exitosos.length }})</span>
                <ul>
                  <li v-for="(item, idx) in imgResultados.exitosos" :key="'ok' + idx">
                    Alojamiento {{ item.idalojamiento }}: {{ item.archivo }}
                  </li>
                </ul>
              </div>
              <div v-if="imgResultados.errores.length" class="cm-results-block">
                <span class="cm-results-title err">Errores ({{ imgResultados.errores.length }})</span>
                <ul>
                  <li v-for="(err, idx) in imgResultados.errores" :key="'err' + idx">{{ err }}</li>
                </ul>
              </div>
            </div>
          </div>

          <!-- PANEL: EXCEL -->
          <div v-else-if="activeMode === 'excel'" class="cm-panel">
            <h2>Carga masiva por Excel</h2>
            <p class="cm-panel-desc">Subí un archivo CSV (.csv) para crear alojamientos en masa. Descargá la plantilla, completala y subila aquí.</p>
            <p class="cm-panel-desc">La columna <b>ical_url</b> es opcional: si la completás, el calendario externo queda asociado al alojamiento en el momento de la carga. Las fechas ocupadas no aparecen al instante — las trae la sincronización de iCal, que corre cada hora.</p>

            <div class="cm-actions">
              <label class="cm-file">
                <input ref="excelInput" type="file" @change="handleExcelSelect" accept=".csv,text/csv,application/vnd.ms-excel" hidden />
                <i class="fas fa-upload"></i> Elegir archivo CSV
              </label>
              <button class="cm-btn cm-btn-ghost" @click="downloadTemplate"><i class="fas fa-download"></i> Descargar plantilla</button>
            </div>

            <div v-if="xlsError" class="cm-alert cm-alert-error">{{ xlsError }}</div>
            <div v-if="xlsSuccess" class="cm-alert cm-alert-ok">Carga finalizada.</div>

            <div v-if="xlsResultados.exitosos.length || xlsResultados.errores.length" class="cm-results">
              <div v-if="xlsResultados.exitosos.length" class="cm-results-block">
                <span class="cm-results-title ok">Alojamientos procesados ({{ xlsResultados.exitosos.length }})</span>
                <ul>
                  <li v-for="(item, idx) in xlsResultados.exitosos" :key="'xok' + idx">{{ item }}</li>
                </ul>
              </div>
              <div v-if="xlsResultados.errores.length" class="cm-results-block">
                <span class="cm-results-title err">Errores ({{ xlsResultados.errores.length }})</span>
                <ul>
                  <li v-for="(err, idx) in xlsResultados.errores" :key="'xerr' + idx">{{ err }}</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        <!-- ============ SIDEBAR ============ -->
        <aside class="cm-side">
          <!-- BÚSQUEDA DE USUARIO -->
          <div class="cm-side-card">
            <span class="cm-side-label">Herramienta</span>
            <h3>Búsqueda de usuario</h3>
            <form class="cm-side-form" @submit.prevent="buscarUsuario">
              <input v-model="busqueda" type="text" placeholder="ID de usuario o email" class="cm-input" />
              <button type="submit" class="cm-btn cm-btn-sm">Buscar</button>
            </form>
            <div v-if="userError" class="cm-alert cm-alert-error">{{ userError }}</div>

            <div v-if="usuarioEncontrado" class="cm-user">
              <div class="cm-user-top">
                <span class="cm-side-label">Datos del usuario</span>
                <button class="cm-btn cm-btn-sm cm-btn-ghost" :disabled="exporting" @click="exportUserAccommodations">
                  {{ exporting ? 'Exportando...' : 'Exportar' }}
                </button>
              </div>
              <table class="cm-user-table">
                <tbody>
                  <tr v-for="(valor, clave) in usuarioEncontrado" :key="clave">
                    <th>{{ clave }}</th>
                    <td>{{ valor }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <!-- DIRECCIÓN GOOGLE -->
          <div class="cm-side-card">
            <span class="cm-side-label">Herramienta</span>
            <h3>Dirección (Google)</h3>
            <p class="cm-side-desc">Escribí una dirección y seleccioná una opción para ver su latitud y longitud.</p>
            <div v-if="isGoogleMapsLoaded">
              <vue-google-autocomplete
                id="cm_address_autocomplete"
                class="cm-input"
                placeholder="Ej: Av. Providencia 1234, Santiago"
                v-model="direccionSeleccionada"
                v-on:placechanged="handleAddressSelected"
                v-on:error="handleGoogleAutocompleteError"
              ></vue-google-autocomplete>
            </div>
            <div v-else class="cm-hint">Cargando Google Maps...</div>

            <div v-if="latitudSeleccionada !== null && longitudSeleccionada !== null" class="cm-latlng">
              <div class="cm-latlng-item">
                <span class="cm-side-label">Latitud</span>
                <input class="cm-input" type="text" :value="latitudSeleccionada" readonly />
              </div>
              <div class="cm-latlng-item">
                <span class="cm-side-label">Longitud</span>
                <input class="cm-input" type="text" :value="longitudSeleccionada" readonly />
              </div>
            </div>
          </div>
        </aside>
      </div>

      <!-- ============ MODAL PREVIEW EXCEL ============ -->
      <transition name="cm-fade">
        <div v-if="showPreview" class="cm-overlay" @click.self="cancelPreview">
          <div class="cm-modal">
            <header class="cm-modal-head">
              <div>
                <span class="cm-side-label">Vista previa · Carga por Excel</span>
                <h2>Preview de <span class="serif">alojamientos</span></h2>
              </div>
              <button class="cm-modal-x" @click="cancelPreview" aria-label="Cerrar"><i class="fas fa-times"></i></button>
            </header>

            <div class="cm-modal-meta">
              <span v-if="parsing" class="cm-chip loading"><i class="fas fa-circle-notch fa-spin"></i> Cargando preview... {{ previewRows.length }} filas</span>
              <span v-else class="cm-chip ok"><i class="fas fa-check"></i> {{ previewRows.length }} alojamientos</span>
              <span class="cm-chip">{{ previewHeader.length }} columnas</span>
              <span class="cm-modal-hint">Podés editar cualquier celda antes de subir.</span>
            </div>

            <div class="cm-table-wrap">
              <table class="cm-table">
                <thead>
                  <tr>
                    <th class="cm-th-idx">#</th>
                    <th v-for="(col, ci) in previewHeader" :key="'h' + ci">{{ col }}</th>
                    <th class="cm-th-act"></th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(row, ri) in previewRows" :key="'r' + ri">
                    <td class="cm-td-idx">{{ ri + 1 }}</td>
                    <td v-for="(cell, ci) in row" :key="'c' + ri + '-' + ci">
                      <input class="cm-cell" v-model="previewRows[ri][ci]" />
                    </td>
                    <td class="cm-td-act">
                      <button class="cm-row-del" @click="removeRow(ri)" title="Quitar fila"><i class="fas fa-trash-alt"></i></button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div v-if="xlsError" class="cm-alert cm-alert-error cm-modal-alert">{{ xlsError }}</div>

            <footer class="cm-modal-foot">
              <div class="cm-foot-info">
                <span v-if="confirmUploading"><i class="fas fa-circle-notch fa-spin"></i> Subiendo alojamientos...</span>
                <span v-else-if="parsing">Leyendo archivo...</span>
                <span v-else><strong>{{ previewRows.length }}</strong> alojamientos a subir</span>
              </div>
              <div class="cm-foot-actions">
                <button class="cm-btn cm-btn-ghost" :disabled="confirmUploading" @click="cancelPreview">Cancelar</button>
                <button class="cm-btn" :disabled="parsing || confirmUploading || previewRows.length === 0" @click="confirmUpload">
                  {{ confirmUploading ? 'Subiendo...' : 'Subir alojamientos' }}
                </button>
              </div>
            </footer>
          </div>
        </div>
      </transition>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import apiUrls from '@/new-constants/apiUrls';
import VueGoogleAutocomplete from 'vue-google-autocomplete';

export default {
  name: 'CargaMasiva',
  components: { VueGoogleAutocomplete },
  data() {
    return {
      activeMode: null, // 'images' | 'excel'

      // Imágenes
      selectedFiles: [],
      imgUploading: false,
      imgError: '',
      imgSuccess: false,
      imgResultados: { exitosos: [], errores: [], procesados: 0 },

      // Excel
      xlsError: '',
      xlsSuccess: false,
      xlsResultados: { exitosos: [], errores: [] },

      // Preview modal (Excel)
      showPreview: false,
      parsing: false,
      confirmUploading: false,
      previewHeader: [],
      previewRows: [],

      // Búsqueda de usuario
      busqueda: '',
      usuarioEncontrado: null,
      userError: '',
      exporting: false,

      // Dirección Google
      isGoogleMapsLoaded: false,
      direccionSeleccionada: '',
      latitudSeleccionada: null,
      longitudSeleccionada: null,
    };
  },
  methods: {
    /* ---------- Imágenes ---------- */
    handleFilesSelect(event) {
      const incoming = Array.from(event.target.files || []);
      if (!incoming.length) return;
      const mapKey = f => `${f.name}::${f.size}`;
      const existing = new Set(this.selectedFiles.map(mapKey));
      incoming.forEach(f => { if (!existing.has(mapKey(f))) this.selectedFiles.push(f); });
      event.target.value = '';
    },
    clearSelection() {
      this.selectedFiles = [];
      this.imgSuccess = false;
      this.imgError = '';
      this.imgResultados = { exitosos: [], errores: [], procesados: 0 };
    },
    async uploadSelectedFiles() {
      const files = this.selectedFiles;
      if (!files.length) return;

      const MAX = 100;
      if (files.length > MAX) {
        this.imgError = `Máximo ${MAX} imágenes por carga.`;
        return;
      }

      this.imgUploading = true;
      this.imgError = '';
      this.imgSuccess = false;
      this.imgResultados = { exitosos: [], errores: [], procesados: 0 };

      const formData = new FormData();
      files.forEach(f => formData.append('files[]', f));

      const token = localStorage.getItem('token');
      try {
        const resp = await axios.post(
          `${process.env.VUE_APP_API_URL}accommodations/bulk-upload-images`,
          formData,
          { headers: { 'Content-Type': 'multipart/form-data', 'Authorization': token || '' } }
        );
        this.imgSuccess = true;
        this.imgResultados = {
          exitosos: resp.data.exitosos || [],
          errores: resp.data.errores || [],
          procesados: resp.data.procesados || 0
        };
      } catch (e) {
        if (e.response && e.response.data) {
          this.imgError = e.response.data.message || 'Error en la carga.';
          this.imgResultados = {
            exitosos: e.response.data.exitosos || [],
            errores: e.response.data.errores || [],
            procesados: e.response.data.procesados || 0
          };
        } else {
          this.imgError = e.message || 'Error al subir imágenes.';
        }
      } finally {
        this.imgUploading = false;
      }
    },

    /* ---------- Excel: seleccionar y parsear ---------- */
    handleExcelSelect(event) {
      const file = event.target.files[0];
      if (!file) return;
      const fileName = file.name.toLowerCase();
      const allowedTypes = ['text/csv', 'application/vnd.ms-excel'];
      const isValidType = allowedTypes.includes(file.type) || fileName.endsWith('.csv');
      if (!isValidType) {
        this.xlsError = 'Solo se permiten archivos .csv';
        return;
      }
      // Reset de estado previo
      this.xlsError = '';
      this.xlsSuccess = false;
      this.xlsResultados = { exitosos: [], errores: [] };
      this.parseCsvFile(file);
      // Permitir volver a elegir el mismo archivo
      if (this.$refs.excelInput) this.$refs.excelInput.value = '';
    },
    parseCsvFile(file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        let text = (e.target && e.target.result) || '';
        text = text.replace(/^﻿/, ''); // quitar BOM
        // Se parsea el texto completo (no línea por línea) porque un campo entre
        // comillas puede contener saltos de línea (p. ej. textos largos guardados
        // desde Excel). Partir por '\n' antes convertía cada párrafo en una fila.
        const records = this.parseCsvText(text)
          .filter(r => r.some(v => String(v).trim() !== ''));
        if (!records.length) {
          this.xlsError = 'El archivo está vacío.';
          return;
        }
        this.previewHeader = records[0].map(h => h.trim());
        this.previewRows = [];
        this.parsing = true;
        this.showPreview = true;

        // Llenado "en vivo" por lotes para mostrar el preview a medida que carga
        const dataRows = records.slice(1);
        let i = 0;
        const CHUNK = 20;
        const pump = () => {
          const end = Math.min(i + CHUNK, dataRows.length);
          for (; i < end; i++) {
            this.previewRows.push(dataRows[i]);
          }
          if (i < dataRows.length) {
            requestAnimationFrame(pump);
          } else {
            this.parsing = false;
          }
        };
        requestAnimationFrame(pump);
      };
      reader.onerror = () => { this.xlsError = 'No se pudo leer el archivo.'; };
      reader.readAsText(file);
    },
    // Parseo de una línea CSV con delimitador ';' respetando comillas dobles
    splitCsvLine(line) {
      const out = [];
      let cur = '';
      let inQuotes = false;
      for (let k = 0; k < line.length; k++) {
        const ch = line[k];
        if (inQuotes) {
          if (ch === '"') {
            if (line[k + 1] === '"') { cur += '"'; k++; }
            else { inQuotes = false; }
          } else { cur += ch; }
        } else {
          if (ch === '"') inQuotes = true;
          else if (ch === ';') { out.push(cur); cur = ''; }
          else cur += ch;
        }
      }
      out.push(cur);
      return out;
    },
    // Parseo del CSV completo (delimitador ';'). Respeta comillas dobles,
    // comillas escapadas ("") y saltos de línea dentro de campos entre comillas.
    parseCsvText(text) {
      const rows = [];
      let row = [];
      let cur = '';
      let inQuotes = false;
      for (let k = 0; k < text.length; k++) {
        const ch = text[k];
        if (inQuotes) {
          if (ch === '"') {
            if (text[k + 1] === '"') { cur += '"'; k++; }
            else { inQuotes = false; }
          } else { cur += ch; }
        } else if (ch === '"') {
          inQuotes = true;
        } else if (ch === ';') {
          row.push(cur); cur = '';
        } else if (ch === '\r' || ch === '\n') {
          if (ch === '\r' && text[k + 1] === '\n') k++;
          row.push(cur); rows.push(row);
          row = []; cur = '';
        } else {
          cur += ch;
        }
      }
      if (cur !== '' || row.length) { row.push(cur); rows.push(row); }
      return rows;
    },
    removeRow(index) {
      this.previewRows.splice(index, 1);
    },
    cancelPreview() {
      if (this.confirmUploading) return;
      this.showPreview = false;
      this.parsing = false;
      this.previewHeader = [];
      this.previewRows = [];
      this.xlsError = '';
    },
    // Reconstruye el CSV desde la tabla editada (delimitador ';', comillas si hace falta)
    buildCsvFromPreview() {
      const esc = (v) => {
        const s = v == null ? '' : String(v);
        return /[;"\n\r]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s;
      };
      const lines = [];
      lines.push(this.previewHeader.map(esc).join(';'));
      this.previewRows.forEach(r => lines.push(r.map(esc).join(';')));
      return lines.join('\n');
    },
    async confirmUpload() {
      if (!this.previewRows.length || this.confirmUploading) return;
      this.confirmUploading = true;
      this.xlsError = '';
      this.xlsSuccess = false;
      this.xlsResultados = { exitosos: [], errores: [] };

      const csv = this.buildCsvFromPreview();
      const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
      const formData = new FormData();
      formData.append('file', blob, 'carga_masiva.csv');

      const token = localStorage.getItem('token');
      try {
        const resp = await axios.post(
          `${process.env.VUE_APP_API_URL}accommodations/bulk-upload-excel`,
          formData,
          { headers: { 'Content-Type': 'multipart/form-data', 'Authorization': token || '' } }
        );
        this.xlsSuccess = true;
        this.xlsResultados = {
          exitosos: resp.data.exitosos || [],
          errores: resp.data.errores || []
        };
        // Cierre del modal; los resultados quedan visibles en el panel
        this.showPreview = false;
        this.previewHeader = [];
        this.previewRows = [];
      } catch (e) {
        if (e.response && e.response.data) {
          this.xlsError = e.response.data.message || 'Error al subir los alojamientos.';
          this.xlsResultados = {
            exitosos: e.response.data.exitosos || [],
            errores: e.response.data.errores || []
          };
        } else {
          this.xlsError = e.message || 'Error al subir los alojamientos.';
        }
        // Se mantiene el modal abierto para corregir y reintentar
      } finally {
        this.confirmUploading = false;
      }
    },
    downloadTemplate() {
      window.open('/plantillas/ejemplo_masivo.csv', '_blank');
    },

    /* ---------- Búsqueda de usuario ---------- */
    async buscarUsuario() {
      this.usuarioEncontrado = null;
      this.userError = '';
      if (!this.busqueda) {
        this.userError = 'Ingresá un ID de usuario o email.';
        return;
      }
      try {
        const token = localStorage.getItem('token');
        const resp = await axios.get(`${process.env.VUE_APP_API_URL}admin/find-user`, {
          params: { query: this.busqueda },
          headers: { 'Authorization': token || '' }
        });
        if (resp.data && resp.data.usuario) {
          this.usuarioEncontrado = resp.data.usuario;
        } else {
          this.userError = 'Usuario no encontrado.';
        }
      } catch (e) {
        this.userError = (e.response && e.response.data && e.response.data.message) ? e.response.data.message : 'Error al buscar usuario.';
      }
    },
    async exportUserAccommodations() {
      if (!this.usuarioEncontrado || !this.usuarioEncontrado.idusuario) return;
      this.exporting = true;
      this.userError = '';
      try {
        const token = localStorage.getItem('token');
        const url = `${apiUrls.adminExportAccommodationsByUser}${this.usuarioEncontrado.idusuario}`;
        const resp = await axios.get(url, { headers: { 'Authorization': token || '' } });
        const data = Array.isArray(resp.data) ? resp.data : (resp.data.data || []);

        if (!data || data.length === 0) {
          this.userError = 'No se encontraron alojamientos para exportar.';
          return;
        }

        const ws = XLSX.utils.json_to_sheet(data);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Alojamientos');
        const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
        const blob = new Blob([wbout], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        saveAs(blob, `alojamientos_usuario_${this.usuarioEncontrado.idusuario}.xlsx`);
      } catch (e) {
        this.userError = (e.response && e.response.data && e.response.data.message) ? e.response.data.message : (e.message || 'Error al exportar.');
      } finally {
        this.exporting = false;
      }
    },

    /* ---------- Dirección Google ---------- */
    handleAddressSelected(addressData, placeResultData) {
      if (addressData && typeof addressData.latitude !== 'undefined' && typeof addressData.longitude !== 'undefined') {
        this.latitudSeleccionada = addressData.latitude;
        this.longitudSeleccionada = addressData.longitude;
      }
      if (placeResultData && placeResultData.formatted_address) {
        this.direccionSeleccionada = placeResultData.formatted_address;
      }
    },
    handleGoogleAutocompleteError() {
      // Silencioso, como en las demás pantallas.
    },
  },
  mounted() {
    if (window.googleMapsLoaded && typeof window.googleMapsLoaded.then === 'function') {
      window.googleMapsLoaded
        .then(() => { this.isGoogleMapsLoaded = true; })
        .catch(() => { this.isGoogleMapsLoaded = false; });
    } else {
      this.isGoogleMapsLoaded = typeof window.google !== 'undefined';
    }
  }
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Geist:wght@300;400;500;600;700;800&family=Instrument+Serif:ital@0;1&display=swap');

.cm {
  --orange: #FC7010;
  --magenta: #B91E7C;
  --bg: #FFFFFF;
  --bg-subtle: #FAFAF9;
  --bg-muted: #F4F3F0;
  --border: #ECEAE4;
  --border-strong: #DBD9D2;
  --text: #0A0A0B;
  --text-mid: #383840;
  --text-dim: #6E6E76;
  --text-faint: #A0A0A8;
  --green: #003D29;

  --hyf-display: 'Geist', -apple-system, BlinkMacSystemFont, sans-serif;
  --hyf-serif: 'Instrument Serif', serif;

  font-family: var(--hyf-display);
  color: var(--text);
  background: var(--bg-subtle);
  min-height: 100%;
  letter-spacing: -0.01em;
  -webkit-font-smoothing: antialiased;
  padding: 40px 24px 64px;
}
.cm *, .cm *::before, .cm *::after { box-sizing: border-box; }

.cm-shell { max-width: 1180px; margin: 0 auto; }

/* HEADER */
.cm-head { margin-bottom: 40px; }
.cm-eyebrow {
  font-family: var(--hyf-display); font-size: 12px; font-weight: 600;
  letter-spacing: 0.08em; text-transform: uppercase; color: var(--orange);
  display: inline-flex; align-items: center; gap: 8px; margin-bottom: 14px;
}
.cm-eyebrow::before { content: ''; width: 6px; height: 6px; border-radius: 50%; background: var(--orange); }
.cm-head h1 {
  font-family: var(--hyf-display); font-size: clamp(38px, 5vw, 60px);
  font-weight: 600; line-height: 1.0; letter-spacing: -0.045em; color: var(--text); margin: 0;
}
.cm-head h1 .serif {
  font-family: var(--hyf-serif); font-style: italic; font-weight: 400;
  background: linear-gradient(135deg, var(--orange) 0%, var(--magenta) 100%);
  -webkit-background-clip: text; -webkit-text-fill-color: transparent; background-clip: text;
}
.cm-sub { font-size: 18px; line-height: 1.5; color: var(--text-dim); font-weight: 400; margin-top: 16px; max-width: 640px; }

/* GRID */
.cm-grid { display: grid; grid-template-columns: minmax(0, 1.55fr) minmax(320px, 1fr); gap: 28px; align-items: start; }
@media (max-width: 940px) { .cm-grid { grid-template-columns: 1fr; } }

/* CARDS SELECTOR */
.cm-cards { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; margin-bottom: 24px; }
@media (max-width: 560px) { .cm-cards { grid-template-columns: 1fr; } }
.cm-card {
  display: flex; align-items: center; gap: 14px; text-align: left;
  background: var(--bg); border: 1px solid var(--border); border-radius: 18px;
  padding: 20px; cursor: pointer; transition: all 0.16s ease; font-family: inherit;
  box-shadow: 0 1px 2px rgba(10,10,11,0.03);
}
.cm-card:hover { border-color: var(--border-strong); transform: translateY(-2px); box-shadow: 0 8px 24px rgba(10,10,11,0.06); }
.cm-card.active { border-color: var(--orange); box-shadow: 0 8px 28px rgba(252,112,16,0.14); }
.cm-card-icon {
  flex-shrink: 0; width: 46px; height: 46px; border-radius: 12px;
  display: flex; align-items: center; justify-content: center; font-size: 20px; color: #fff;
  background: linear-gradient(135deg, var(--orange) 0%, var(--magenta) 100%);
}
.cm-card-body { display: flex; flex-direction: column; gap: 3px; min-width: 0; }
.cm-card-body strong { font-size: 16px; font-weight: 600; letter-spacing: -0.02em; color: var(--text); }
.cm-card-body span { font-size: 13px; line-height: 1.4; color: var(--text-dim); }
.cm-card-arrow { margin-left: auto; color: var(--text-faint); font-size: 13px; transition: all 0.16s; }
.cm-card.active .cm-card-arrow { color: var(--orange); transform: translateX(2px); }

/* PANEL */
.cm-panel { background: var(--bg); border: 1px solid var(--border); border-radius: 18px; padding: 28px; box-shadow: 0 1px 2px rgba(10,10,11,0.03); }
.cm-panel h2 { font-family: var(--hyf-display); font-size: 24px; font-weight: 600; letter-spacing: -0.03em; margin: 0 0 8px; }
.cm-panel-desc { font-size: 14px; line-height: 1.55; color: var(--text-dim); margin: 0 0 20px; }
.cm-panel-desc b { color: var(--text-mid); font-weight: 600; }

.cm-empty { display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 12px; text-align: center; padding: 56px 28px; color: var(--text-faint); }
.cm-empty i { font-size: 28px; }
.cm-empty p { margin: 0; font-size: 15px; }

/* ACTIONS */
.cm-actions { display: flex; flex-wrap: wrap; gap: 12px; align-items: center; margin-bottom: 8px; }
.cm-file {
  display: inline-flex; align-items: center; gap: 8px; cursor: pointer;
  background: var(--bg-muted); border: 1px solid var(--border); border-radius: 999px;
  padding: 10px 18px; font-size: 14px; font-weight: 500; color: var(--text); transition: all 0.15s;
}
.cm-file:hover { border-color: var(--text); }
.cm-btn {
  display: inline-flex; align-items: center; gap: 8px; cursor: pointer;
  background: var(--green); color: #fff; border: 1px solid var(--green);
  border-radius: 999px; padding: 10px 20px; font-size: 14px; font-weight: 500;
  font-family: inherit; transition: all 0.15s; letter-spacing: -0.01em;
}
.cm-btn:hover:not(:disabled) { filter: brightness(1.12); }
.cm-btn:disabled { opacity: 0.5; cursor: not-allowed; }
.cm-btn-sm { padding: 9px 16px; font-size: 13px; }
.cm-btn-ghost { background: transparent; color: var(--text); border-color: var(--border-strong); }
.cm-btn-ghost:hover:not(:disabled) { border-color: var(--text); filter: none; }

.cm-hint { font-size: 13px; color: var(--text-dim); margin-top: 12px; }
.cm-hint strong { color: var(--text); font-weight: 600; }

/* ALERTS */
.cm-alert { margin-top: 14px; padding: 12px 16px; border-radius: 12px; font-size: 13px; line-height: 1.45; }
.cm-alert-error { background: #FDECEC; border: 1px solid #F5C2C2; color: #A11E1E; }
.cm-alert-ok { background: #E9F7EF; border: 1px solid #B6E2C8; color: #1B7A45; }

/* RESULTS */
.cm-results { margin-top: 20px; display: flex; flex-direction: column; gap: 16px; }
.cm-results-block { border: 1px solid var(--border); border-radius: 12px; padding: 14px 16px; background: var(--bg-subtle); }
.cm-results-title { display: inline-block; font-size: 12px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.05em; margin-bottom: 8px; }
.cm-results-title.ok { color: #1B7A45; }
.cm-results-title.err { color: #A11E1E; }
.cm-results ul { margin: 0; padding-left: 18px; }
.cm-results li { font-size: 13px; line-height: 1.55; color: var(--text-mid); word-break: break-word; }

/* SIDEBAR */
.cm-side { display: flex; flex-direction: column; gap: 20px; }
.cm-side-card { background: var(--bg); border: 1px solid var(--border); border-radius: 18px; padding: 22px; box-shadow: 0 1px 2px rgba(10,10,11,0.03); }
.cm-side-label { font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.07em; color: var(--text-faint); display: block; margin-bottom: 6px; }
.cm-side-card h3 { font-family: var(--hyf-display); font-size: 19px; font-weight: 600; letter-spacing: -0.025em; margin: 0 0 14px; }
.cm-side-desc { font-size: 13px; line-height: 1.5; color: var(--text-dim); margin: 0 0 14px; }
.cm-side-form { display: flex; gap: 8px; }
.cm-input {
  width: 100%; background: var(--bg-subtle); border: 1px solid var(--border-strong);
  border-radius: 10px; padding: 10px 14px; font-size: 14px; font-family: inherit;
  color: var(--text); transition: border-color 0.15s; letter-spacing: -0.01em;
}
.cm-input:focus { outline: none; border-color: var(--orange); }

.cm-user { margin-top: 18px; }
.cm-user-top { display: flex; align-items: center; justify-content: space-between; gap: 10px; margin-bottom: 10px; }
.cm-user-table { width: 100%; border-collapse: collapse; font-size: 13px; }
.cm-user-table th, .cm-user-table td { text-align: left; padding: 7px 10px; border: 1px solid var(--border); vertical-align: top; word-break: break-word; }
.cm-user-table th { background: var(--bg-subtle); color: var(--text-dim); font-weight: 600; width: 42%; }

.cm-latlng { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; margin-top: 14px; }
.cm-latlng-item { display: flex; flex-direction: column; gap: 4px; }

/* ============ MODAL PREVIEW ============ */
.cm-overlay {
  position: fixed; inset: 0; z-index: 1000;
  background: rgba(10, 10, 11, 0.42);
  backdrop-filter: saturate(140%) blur(6px);
  -webkit-backdrop-filter: saturate(140%) blur(6px);
  display: flex; align-items: center; justify-content: center; padding: 28px;
}
.cm-modal {
  display: flex; flex-direction: column;
  width: 100%; max-width: 1120px; max-height: 88vh;
  background: var(--bg); border: 1px solid var(--border); border-radius: 22px;
  box-shadow: 0 30px 80px rgba(10, 10, 11, 0.28); overflow: hidden;
}
.cm-modal-head {
  display: flex; align-items: flex-start; justify-content: space-between; gap: 16px;
  padding: 24px 28px 18px; border-bottom: 1px solid var(--border);
}
.cm-modal-head h2 { font-family: var(--hyf-display); font-size: 26px; font-weight: 600; letter-spacing: -0.035em; margin: 4px 0 0; }
.cm-modal-head h2 .serif {
  font-family: var(--hyf-serif); font-style: italic; font-weight: 400;
  background: linear-gradient(135deg, var(--orange) 0%, var(--magenta) 100%);
  -webkit-background-clip: text; -webkit-text-fill-color: transparent; background-clip: text;
}
.cm-modal-x {
  flex-shrink: 0; width: 38px; height: 38px; border-radius: 10px; cursor: pointer;
  background: var(--bg-subtle); border: 1px solid var(--border); color: var(--text-dim);
  font-size: 15px; transition: all 0.15s;
}
.cm-modal-x:hover { border-color: var(--text); color: var(--text); }

.cm-modal-meta { display: flex; flex-wrap: wrap; align-items: center; gap: 10px; padding: 16px 28px; }
.cm-chip {
  display: inline-flex; align-items: center; gap: 6px; padding: 5px 12px; border-radius: 999px;
  font-size: 12px; font-weight: 500; letter-spacing: -0.01em;
  background: var(--bg-muted); border: 1px solid var(--border); color: var(--text-mid);
}
.cm-chip.loading { background: #FFF1E5; border-color: #FFD4AE; color: #B84800; }
.cm-chip.ok { background: #E9F7EF; border-color: #B6E2C8; color: #1B7A45; }
.cm-modal-hint { font-size: 12px; color: var(--text-faint); }

/* Tabla editable */
.cm-table-wrap { flex: 1; overflow: auto; margin: 0 28px; border: 1px solid var(--border); border-radius: 12px; }
.cm-table { border-collapse: separate; border-spacing: 0; font-size: 12px; white-space: nowrap; }
.cm-table thead th {
  position: sticky; top: 0; z-index: 2;
  background: var(--bg-muted); color: var(--text-dim);
  font-weight: 600; text-transform: none; letter-spacing: 0;
  padding: 9px 10px; border-bottom: 1px solid var(--border-strong); text-align: left;
}
.cm-table th.cm-th-idx, .cm-table th.cm-th-act { position: sticky; z-index: 3; }
.cm-table th.cm-th-idx, .cm-table td.cm-td-idx { left: 0; }
.cm-table th.cm-th-idx { left: 0; z-index: 4; }
.cm-td-idx {
  position: sticky; left: 0; z-index: 1; background: var(--bg-subtle);
  color: var(--text-faint); font-weight: 600; text-align: center;
  padding: 0 10px; border-bottom: 1px solid var(--border); border-right: 1px solid var(--border);
}
.cm-th-idx { min-width: 44px; text-align: center; }
.cm-td-act, .cm-th-act { text-align: center; padding: 0 6px; border-bottom: 1px solid var(--border); }
.cm-table tbody td { border-bottom: 1px solid var(--border); padding: 0; }
.cm-table tbody tr:hover td { background: rgba(252, 112, 16, 0.03); }
.cm-cell {
  border: none; background: transparent; width: 100%; min-width: 110px; max-width: 260px;
  padding: 8px 10px; font-size: 12px; font-family: inherit; color: var(--text);
  letter-spacing: -0.01em;
}
.cm-cell:focus { outline: 2px solid var(--orange); outline-offset: -2px; background: #fff; border-radius: 4px; }
.cm-row-del {
  width: 26px; height: 26px; border-radius: 7px; cursor: pointer; font-size: 11px;
  background: transparent; border: 1px solid transparent; color: var(--text-faint); transition: all 0.14s;
}
.cm-row-del:hover { background: #FDECEC; border-color: #F5C2C2; color: #A11E1E; }

.cm-modal-alert { margin: 14px 28px 0; }

.cm-modal-foot {
  display: flex; align-items: center; justify-content: space-between; gap: 16px;
  padding: 18px 28px 22px; border-top: 1px solid var(--border); margin-top: 16px;
}
.cm-foot-info { font-size: 13px; color: var(--text-dim); }
.cm-foot-info strong { color: var(--text); font-weight: 600; }
.cm-foot-actions { display: flex; gap: 12px; }

/* Transición del modal */
.cm-fade-enter-active, .cm-fade-leave-active { transition: opacity 0.2s ease; }
.cm-fade-enter, .cm-fade-leave-to { opacity: 0; }
.cm-fade-enter .cm-modal, .cm-fade-leave-to .cm-modal { transform: translateY(10px); }

@media (max-width: 640px) {
  .cm-overlay { padding: 0; }
  .cm-modal { max-height: 100vh; border-radius: 0; }
}
</style>
